/**
 * CartDrawer Component
 * Slide-out cart drawer with line items
 */

import { useEffect, type MouseEvent } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useCartStore, selectCartItems, selectCartTotal, selectCartCurrency, selectCheckoutUrl } from '~/store/cart-store';
import { formatPrice } from '~/lib/shopify/api';
import { CartLineItem } from './CartLineItem';
import { trackViewCart, trackBeginCheckout } from '~/lib/tracking/gtm';

export function CartDrawer() {
  const isOpen = useCartStore((state) => state.isOpen);
  const closeCart = useCartStore((state) => state.closeCart);
  const isLoading = useCartStore((state) => state.isLoading);
  const items = useCartStore(selectCartItems);
  const total = useCartStore(selectCartTotal);
  const currency = useCartStore(selectCartCurrency);
  const checkoutUrl = useCartStore(selectCheckoutUrl);

  const cart = useCartStore((state) => state.cart);
  const isEmpty = items.length === 0;

  // Track view_cart when drawer opens with items
  useEffect(() => {
    if (isOpen && cart && cart.lines.edges.length > 0) {
      trackViewCart(cart);
    }
  }, [isOpen]);

  const handleCheckoutClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (cart) trackBeginCheckout(cart);
    if (checkoutUrl) window.location.href = checkoutUrl;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={closeCart}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-surface-coal border-l border-surface-slate z-50 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-surface-slate">
              <h2 className="font-display font-bold text-xl text-accent-white">
                Your Cart
                {items.length > 0 && (
                  <span className="ml-2 text-accent-silver font-normal text-base">
                    ({items.length} {items.length === 1 ? 'item' : 'items'})
                  </span>
                )}
              </h2>
              <button
                onClick={closeCart}
                className="w-10 h-10 flex items-center justify-center text-accent-silver hover:text-accent-white hover:bg-surface-slate rounded-xl transition-colors"
                aria-label="Close cart"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Cart Content */}
            <div className="flex-1 overflow-y-auto">
              {isEmpty ? (
                <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                  <div className="w-20 h-20 bg-surface-graphite rounded-2xl flex items-center justify-center mb-6">
                    <svg className="w-10 h-10 text-accent-silver" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-accent-white mb-2">Your cart is empty</h3>
                  <p className="text-accent-silver mb-6">Looks like you haven't added any items yet.</p>
                  <button onClick={closeCart} className="btn-primary">
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="p-6 space-y-4">
                  {items.map((item) => (
                    <CartLineItem key={item.id} item={item} />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {!isEmpty && (
              <div className="border-t border-surface-slate p-6 space-y-4">
                {/* Free Shipping Progress */}
                <FreeShippingProgress total={parseFloat(total)} />

                {/* Subtotal */}
                <div className="flex justify-between items-center">
                  <span className="text-accent-silver">Subtotal <span className="text-xs">(inc. VAT)</span></span>
                  <span className="text-xl font-display font-bold text-accent-white">
                    {formatPrice(total, currency)}
                  </span>
                </div>

                <p className="text-xs text-accent-platinum">
                  All prices include VAT. Shipping calculated at checkout.
                </p>

                {/* Checkout Button */}
                <a
                  href={checkoutUrl || '#'}
                  onClick={handleCheckoutClick}
                  className="btn-primary w-full py-4 text-base"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Updating...
                    </span>
                  ) : (
                    'Proceed to Checkout'
                  )}
                </a>

                {/* Continue Shopping */}
                <button
                  onClick={closeCart}
                  className="w-full text-center text-sm text-accent-silver hover:text-accent-white transition-colors"
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function FreeShippingProgress({ total }: { total: number }) {
  const threshold = 50;
  const remaining = Math.max(0, threshold - total);
  const progress = Math.min(100, (total / threshold) * 100);

  if (remaining <= 0) {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 flex items-center gap-3">
        <span className="text-xl">🎉</span>
        <span className="text-sm text-emerald-400 font-medium">
          You've unlocked free next-day delivery!
        </span>
      </div>
    );
  }

  return (
    <div className="bg-surface-graphite rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-accent-silver">Free next-day delivery</span>
        <span className="text-sm font-medium text-accent-white">
          £{remaining.toFixed(2)} away
        </span>
      </div>
      <div className="h-2 bg-surface-slate rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="h-full bg-brand-orange rounded-full"
        />
      </div>
    </div>
  );
}
